import { useEffect, useState } from 'react';
import { ChevronRight, FileText, Package, Plus, Trash2 } from 'lucide-react';
import { api } from '../lib/api.js';
import { showAlert, showConfirm } from '../lib/tg.js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemGroup,
  ItemGroupContent,
  ItemGroupEmpty,
  ItemGroupHeader,
  ItemHeader,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import { ErrorBanner } from '@/components/feedback';

interface Product {
  id: string;
  name: string;
  slug: string;
  archMd: string;
  updatedAt?: string | null;
}

interface ProductsViewProps {
  onOpenProjects: (productId: string, productName: string) => void;
}

type DialogState =
  | { kind: 'edit'; product: Product | null }
  | { kind: 'arch'; product: Product }
  | null;

export function ProductsView({ onOpenProjects }: ProductsViewProps) {
  const [items, setItems] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);

  const reload = async () => {
    try {
      setItems(await api.get<Product[]>('/api/products'));
    } catch (e) {
      setError((e as { message: string }).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  return (
    <ItemGroup>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <ItemGroupHeader
        title="Products"
        description="Top-level work units. Each owns an ARCH.md and contains projects."
        action={
          <Button size="sm" onClick={() => setDialog({ kind: 'edit', product: null })}>
            <Plus />
            New
          </Button>
        }
      />

      <ItemGroupContent>
        {loading && items.length === 0 && <ItemGroupEmpty>loading…</ItemGroupEmpty>}
        {!loading && items.length === 0 && <ItemGroupEmpty>no products yet</ItemGroupEmpty>}

        {items.map((p) => (
          <Item key={p.id} interactive onClick={() => onOpenProjects(p.id, p.name)}>
            <ItemMedia>
              <Package />
            </ItemMedia>
            <ItemContent>
              <ItemHeader>
                <ItemTitle>{p.name}</ItemTitle>
                <code className="text-xs text-muted-foreground">{p.slug}</code>
              </ItemHeader>
            </ItemContent>
            <ItemActions>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDialog({ kind: 'arch', product: p })}
              >
                <FileText />
                ARCH
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDialog({ kind: 'edit', product: p })}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={async () => {
                  if (!(await showConfirm(`Delete product "${p.name}"?`))) return;
                  try {
                    await api.del(`/api/products/${p.id}`);
                    void reload();
                  } catch (e) {
                    void showAlert((e as { message: string }).message);
                  }
                }}
              >
                <Trash2 />
              </Button>
              <ChevronRight className="size-4 text-muted-foreground" />
            </ItemActions>
          </Item>
        ))}
      </ItemGroupContent>

      <ProductEditorDialog
        open={dialog?.kind === 'edit'}
        initial={dialog?.kind === 'edit' ? dialog.product : null}
        onOpenChange={(open) => !open && setDialog(null)}
        onSaved={() => {
          setDialog(null);
          void reload();
        }}
      />

      <ArchEditorDialog
        open={dialog?.kind === 'arch'}
        product={dialog?.kind === 'arch' ? dialog.product : null}
        onOpenChange={(open) => !open && setDialog(null)}
        onSaved={() => {
          setDialog(null);
          void reload();
        }}
      />
    </ItemGroup>
  );
}

function ProductEditorDialog({
  open,
  initial,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  initial: Product | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? '');
    setSlug(initial?.slug ?? '');
    setErr(null);
  }, [open, initial]);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      if (initial) await api.patch(`/api/products/${initial.id}`, { name, slug });
      else await api.post('/api/products', { name, slug, arch_md: '' });
      onSaved();
    } catch (e) {
      setErr((e as { message: string }).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initial ? `Edit "${initial.name}"` : 'New product'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="p-name">Name</Label>
            <Input id="p-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-slug">Slug</Label>
            <Input id="p-slug" value={slug} onChange={(e) => setSlug(e.target.value)} />
          </div>
          {err && <ErrorBanner>{err}</ErrorBanner>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy || !name || !slug} onClick={save}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ArchEditorDialog({
  open,
  product,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  product: Product | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !product) return;
    setBody(product.archMd);
    setErr(null);
  }, [open, product]);

  const save = async () => {
    if (!product) return;
    setBusy(true);
    setErr(null);
    try {
      await api.patch(`/api/products/${product.id}/arch`, { arch_md: body });
      onSaved();
    } catch (e) {
      setErr((e as { message: string }).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{product ? `${product.name} · ARCH.md` : 'ARCH.md'}</DialogTitle>
        </DialogHeader>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="min-h-[400px] font-mono text-xs"
        />
        {err && <ErrorBanner>{err}</ErrorBanner>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={save}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
